import moment from 'moment-timezone';
import { differenceInMinutes } from 'date-fns';
import React from 'react';

import Button, { ButtonLevel } from 'components/Button';
import Buttons from 'components/Buttons';
import Icon from 'components/Icon';
import Menu from 'components/Menu';
import MenuButton from 'components/MenuButton';
import MenuItem from 'components/MenuItem';
import Text from 'components/Text';
import TextBadge, { TextBadgeColor, TextBadgeTextColor } from 'components/TextBadge';
import TextWithIcon from 'components/TextWithIcon';
import TooltipTrigger from 'components/TooltipTrigger';
import Tooltip from 'components/Tooltip';
import { coachingHomeEventPrefix } from 'coaching/components/CoachingHomeView/CoachingHomeView';
import { useCoachingViewState } from 'coaching/components/CoachingView';
import {
  CoachingSessionInstance,
  CoachingSessionTools,
} from 'coaching/components/CoachingHomeView/CoachingSessionsBySubjectContainer';
import { InstanceRowProps } from 'coaching/components/CoachingHomeView/CoachingSessionUpcomingInstanceRows';

type InstanceRowComponentProps = InstanceRowProps & {
  onClickEdit?: () => void;
  onClickCancel?: () => void;
};

const MAX_MINUTES_BEFORE_SESSION_TO_ALLOW_USER_TO_JOIN = 30;
const MAX_MINUTES_BEFORE_LATE_CANCELLATION = 1440;

export function InstanceRowButtons({
  instance,
  sessionTools,
  onClickEdit,
  onClickCancel,
}: Pick<InstanceRowComponentProps, 'instance' | 'sessionTools' | 'onClickEdit' | 'onClickCancel'>) {
  const rescheduleIsDisabled = wouldBeLateCancellation(instance.startDateTime);
  const { sidebarIsOpen } = useCoachingViewState();

  return (
    <Buttons reset="none" align="left">
      <JoinSessionButton instance={instance} sessionTools={sessionTools} />
      {onClickEdit && (
        <MenuButton
          size="small"
          eventKey={`${coachingHomeEventPrefix}.edit_session.click`}
          menu={
            <Menu>
              <MenuItem
                disabled={rescheduleIsDisabled}
                tooltip={
                  rescheduleIsDisabled
                    ? 'You cannot reschedule a session within 24 hours of the scheduled start time'
                    : undefined
                }
                onClick={onClickEdit}
                eventKey={`${coachingHomeEventPrefix}.edit_session_reschedule.click`}
                menuKey="reschedule">
                <TextWithIcon icon="rotate" iconSize="inherit">
                  Reschedule
                </TextWithIcon>
              </MenuItem>
              <MenuItem
                eventKey={`${coachingHomeEventPrefix}.edit_session_cancel.click`}
                onClick={onClickCancel}
                menuKey="cancel">
                <TextWithIcon icon="x" iconSize="inherit">
                  Cancel
                </TextWithIcon>
              </MenuItem>
            </Menu>
          }>
          {sidebarIsOpen ? (
            <Icon name="pencil-stroke" />
          ) : (
            <Text align="center">
              <TextWithIcon gutter="xxsmall" icon="pencil-stroke">
                Edit session
              </TextWithIcon>
            </Text>
          )}
        </MenuButton>
      )}
    </Buttons>
  );
}

export function InstanceDescription({
  instance,
  instanceIsPartOfRecurringSchedule,
  isSelected,
}: {
  instance: CoachingSessionInstance;
  instanceIsPartOfRecurringSchedule: boolean;
  isSelected: boolean;
}) {
  const dateTimeString = constructDateTimeString(instance.startDateTime);
  const coachString = `Coach ${instance.coachName.split(' ')[0]}`;
  const oneOffCoachString = instanceIsPartOfRecurringSchedule ? '' : `with ${coachString} `;
  return (
    <Text
      element="div"
      weight={isSelected ? 'semibold' : 'normal'}
      size="medium"
      color={instance.isCancelled ? 'graphite' : 'chalkboard'}>
      {dateTimeString} {oneOffCoachString}• {instance.duration} min
    </Text>
  );
}

export function InstanceTextBadge({
  isCancelled,
  isLateCancelled,
  isPartOfRecurringSchedule,
  userHasNoRecurringSchedules,
  colorBadgeCancellation = 'paper',
  textBadgeTextColorCancellation = "graphite"
}: {
  isCancelled: boolean;
  isLateCancelled?: boolean;
  isPartOfRecurringSchedule: boolean;
  userHasNoRecurringSchedules: boolean;
  colorBadgeCancellation?: TextBadgeColor;
  textBadgeTextColorCancellation?: TextBadgeTextColor
}) {
  const renderBadge = (text: string, textColorBadge: TextBadgeTextColor, color: TextBadgeColor) => (
    <TextBadge data-test="instance-text-badge" textColor={textColorBadge} color={color} textSize="xxxxsmall">
      {text}
    </TextBadge>
  );

  if (isLateCancelled) {
    return renderBadge('Late Cancellation', textBadgeTextColorCancellation, colorBadgeCancellation);
  } else if (isCancelled) {
    return renderBadge('Cancelled', textBadgeTextColorCancellation, colorBadgeCancellation);
  } else if (isPartOfRecurringSchedule || userHasNoRecurringSchedules) {
    // don't show any badge if the instance is part of a recurring schedule
    // OR if the participant ONLY has one-off instances without a recurring schedule
    return null;
  } else {
    // show `Added` if this is a one-off instance and the user has a recurring schedule
    return renderBadge('Added', 'graphite', 'paper');
  }
  return null;
}

export function JoinSessionButton({
  instance,
  sessionTools,
  buttonLevel = 'secondary',
}: {
  instance: CoachingSessionInstance;
  sessionTools?: CoachingSessionTools;
  buttonLevel?: ButtonLevel;
}) {
  if (!sessionTools) {
    return null;
  }

  const minutesFromNow = differenceInMinutes(instance.startDateTime, new Date());
  const isDisabled = minutesFromNow > MAX_MINUTES_BEFORE_SESSION_TO_ALLOW_USER_TO_JOIN;
  const toUrl = `/video/${sessionTools.distanceLearningSessionUuid}`;
  const joinButton = (
    <Button
      eventKey={`${coachingHomeEventPrefix}.join_session.click`}
      data-test="join-session-button"
      disabled={isDisabled}
      href={toUrl}
      level={buttonLevel}
      size="small"
      target="_blank">
      Join session
    </Button>
  );
  return isDisabled ? (
    <TooltipTrigger
      placement="bottom"
      tooltip={<Tooltip>You can join 30 minutes before the session</Tooltip>}>
      <div className="CoachingSessionsBySubjectContainer-joinSessionButtonTooltip">
        {joinButton}
      </div>
    </TooltipTrigger>
  ) : (
    joinButton
  );
}

export function wouldBeLateCancellation(instanceStartDateTime: string) {
  return (
    differenceInMinutes(instanceStartDateTime, new Date()) < MAX_MINUTES_BEFORE_LATE_CANCELLATION
  );
}

/**
 * Format like Thu, Nov 19 at 7:00PM (PDT)
 */
export function constructDateTimeString(dateTime: string) {
  return moment(dateTime)
    .tz(moment.tz.guess())
    .format('ddd[,] MMM DD [at] LT [(]z[)]');
}
